import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  createCheckoutSession,
  createCustomerPortalSession,
  fetchPlusStatus,
  isBillingConfigured,
  PLUS_PLANS
} from "../services/billing.js";
import { useI18n } from "../i18n.jsx";

const PlusContext = createContext(null);
const MOCK_PLUS_KEY = "sielpa-plus:mock-plus:v1";
const VOCAB_BURST_PREVIEW_LIMIT = 5;

function todayKey(name) {
  return `sielpa-plus:${name}:${new Date().toISOString().slice(0, 10)}`;
}

function readBoolean(key, fallback = false) {
  try {
    return window.localStorage.getItem(key) === "true";
  } catch {
    return fallback;
  }
}

function writeBoolean(key, value) {
  try {
    window.localStorage.setItem(key, value ? "true" : "false");
  } catch {
    // Local storage is optional; the in-memory state still works for the session.
  }
}

function readCount(name) {
  try {
    return Number.parseInt(window.localStorage.getItem(todayKey(name)) || "0", 10) || 0;
  } catch {
    return 0;
  }
}

function writeCount(name, value) {
  try {
    window.localStorage.setItem(todayKey(name), String(value));
  } catch {
    // Ignore unavailable storage.
  }
}

function getBillingReturnState() {
  try {
    return new URLSearchParams(window.location.search).get("sielpa_plus") || "";
  } catch {
    return "";
  }
}

function clearBillingReturnState() {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("sielpa_plus")) return;
    url.searchParams.delete("sielpa_plus");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  } catch {
    // Keeping the return query string is harmless.
  }
}

function getBillingMessage(error, t) {
  if (error?.code === "billing_not_configured") {
    return t("plus.checkoutNotConnected");
  }
  if (error?.code === "billing_endpoint_missing" || error?.code === "billing_network_error") {
    return t("plus.paymentsNotConnected");
  }
  return error?.message || t("plus.checkoutNotReady");
}

function isCanceledPaidThrough(status) {
  return Boolean(status?.isPlus && String(status?.status || "").toLowerCase() === "canceled");
}

function getPlusStatusNotice(status, t, formatDate) {
  if (!status?.isPlus) return t("plus.noActive");
  if (isCanceledPaidThrough(status)) {
    const endLabel = formatDate(status.currentPeriodEnd);
    return endLabel
      ? t("plus.activeUntil", { date: endLabel })
      : t("plus.activeCanceled");
  }
  return t("plus.activeOnDevice");
}

export function PlusProvider({ children }) {
  const { t, formatDate } = useI18n();
  const [mockPlus, setMockPlus] = useState(() => readBoolean(MOCK_PLUS_KEY));
  const [vocabBurstPreviewUsed, setVocabBurstPreviewUsed] = useState(() => readCount("vocabBurst"));
  const [accountStatus, setAccountStatus] = useState(null);
  const [billingBusy, setBillingBusy] = useState(false);
  const [billingError, setBillingError] = useState("");
  const [billingNotice, setBillingNotice] = useState("");
  const [plusSuccessOpen, setPlusSuccessOpen] = useState(false);

  const refreshPlusStatus = useCallback(async ({ source = "manual", quiet = false } = {}) => {
    if (!isBillingConfigured()) {
      if (!quiet) setBillingNotice(t("plus.checkoutNotConnected"));
      return null;
    }

    try {
      if (!quiet) {
        setBillingError("");
        setBillingNotice(t("plus.statusChecking"));
      }
      const status = await fetchPlusStatus({ source });
      setAccountStatus(status);
      if (!quiet) {
        setBillingNotice(getPlusStatusNotice(status, t, formatDate));
      }
      return status;
    } catch (error) {
      if (!quiet) {
        setBillingError(getBillingMessage(error, t));
        setBillingNotice("");
      }
      return null;
    }
  }, [formatDate, t]);

  useEffect(() => {
    const returnState = getBillingReturnState();
    if (returnState === "success") {
      let canceled = false;
      setBillingError("");
      setBillingNotice(t("plus.thanks"));
      const retryDelays = [0, 1200, 2200, 3600];
      retryDelays.forEach((delay, index) => {
        window.setTimeout(async () => {
          if (canceled) return;
          const status = await refreshPlusStatus({ source: index === 0 ? "checkout_success" : "checkout_success_retry", quiet: index > 0 });
          if (canceled || !status?.isPlus) return;
          setBillingNotice(t("plus.welcomeNotice"));
          setPlusSuccessOpen(true);
          canceled = true;
        }, delay);
      });
      clearBillingReturnState();
      return () => {
        canceled = true;
      };
    } else if (returnState === "canceled") {
      setBillingNotice(t("plus.canceledCheckout"));
      clearBillingReturnState();
    } else if (returnState === "portal") {
      refreshPlusStatus({ source: "portal_return" });
      clearBillingReturnState();
    }
  }, [refreshPlusStatus, t]);

  useEffect(() => {
    if (getBillingReturnState() || !isBillingConfigured()) return;
    refreshPlusStatus({ source: "startup", quiet: true });
  }, [refreshPlusStatus]);

  const startCheckout = useCallback(async (interval = "monthly", options = {}) => {
    setBillingBusy(true);
    setBillingError("");
    setBillingNotice("");
    try {
      const { source = "app", ...trace } = options;
      const session = await createCheckoutSession({ interval, source, ...trace });
      window.location.assign(session.url);
    } catch (error) {
      setBillingError(getBillingMessage(error, t));
    } finally {
      setBillingBusy(false);
    }
  }, [t]);

  const openCustomerPortal = useCallback(async (options = {}) => {
    setBillingBusy(true);
    setBillingError("");
    setBillingNotice("");
    try {
      const session = await createCustomerPortalSession({ source: options.source || "app" });
      window.location.assign(session.url);
    } catch (error) {
      setBillingError(getBillingMessage(error, t));
    } finally {
      setBillingBusy(false);
    }
  }, [t]);

  const value = useMemo(() => {
    const isPlus = Boolean(accountStatus?.isPlus || mockPlus);
    const isAdFree = Boolean(accountStatus?.isAdFree || isPlus);
    const subscriptionStatus = String(accountStatus?.status || "").toLowerCase();
    const currentPeriodEndLabel = formatDate(accountStatus?.currentPeriodEnd);
    const canRenewPlus = Boolean(isPlus && subscriptionStatus === "canceled");
    const canManageBilling = Boolean(!canRenewPlus && (isPlus || accountStatus?.hasBillingCustomer));
    const previewRemaining = Math.max(0, VOCAB_BURST_PREVIEW_LIMIT - vocabBurstPreviewUsed);

    function setTestPlus(enabled) {
      setMockPlus(enabled);
      writeBoolean(MOCK_PLUS_KEY, enabled);
    }

    function consumeVocabBurstPreview() {
      if (isPlus) {
        return { ok: true, used: vocabBurstPreviewUsed, limit: VOCAB_BURST_PREVIEW_LIMIT, remaining: previewRemaining, plus: true };
      }
      if (vocabBurstPreviewUsed >= VOCAB_BURST_PREVIEW_LIMIT) {
        return { ok: false, used: vocabBurstPreviewUsed, limit: VOCAB_BURST_PREVIEW_LIMIT, remaining: 0, plus: false };
      }
      const nextUsed = vocabBurstPreviewUsed + 1;
      setVocabBurstPreviewUsed(nextUsed);
      writeCount("vocabBurst", nextUsed);
      return {
        ok: true,
        used: nextUsed,
        limit: VOCAB_BURST_PREVIEW_LIMIT,
        remaining: Math.max(0, VOCAB_BURST_PREVIEW_LIMIT - nextUsed),
        plus: false
      };
    }

    return {
      isPlus,
      isAdFree,
      canRenewPlus,
      canManageBilling,
      subscriptionStatus,
      currentPeriodEnd: accountStatus?.currentPeriodEnd || "",
      currentPeriodEndLabel,
      canUseVocabBurst: isPlus || previewRemaining > 0,
      vocabBurstPreviewLimit: VOCAB_BURST_PREVIEW_LIMIT,
      vocabBurstPreviewUsed,
      vocabBurstPreviewRemaining: previewRemaining,
      accountStatus,
      billingBusy,
      billingError,
      billingNotice,
      billingReady: isBillingConfigured(),
      plusPlans: {
        monthly: { ...PLUS_PLANS.monthly, label: t("billing.monthly") },
        yearly: { ...PLUS_PLANS.yearly, label: t("billing.yearly") }
      },
      startCheckout,
      openCustomerPortal,
      refreshPlusStatus,
      setTestPlus,
      consumeVocabBurstPreview
    };
  }, [
    accountStatus,
    billingBusy,
    billingError,
    billingNotice,
    formatDate,
    mockPlus,
    openCustomerPortal,
    refreshPlusStatus,
    startCheckout,
    t,
    vocabBurstPreviewUsed
  ]);

  return (
    <PlusContext.Provider value={value}>
      {children}
      {billingError ? (
        <div className="plus-global-status is-error" role="alert">
          <span>{billingError}</span>
          <button type="button" onClick={() => setBillingError("")}>{t("plus.dismiss")}</button>
        </div>
      ) : null}
      {plusSuccessOpen ? (
        <div className="plus-upgrade-backdrop" role="presentation">
          <section className="plus-upgrade-dialog plus-success-dialog" role="dialog" aria-modal="true" aria-labelledby="plus-success-title">
            <p className="kicker">SielpA Plus</p>
            <h2 id="plus-success-title">{t("plus.welcomeTitle")}</h2>
            <p>{t("plus.welcomeCopy")}</p>
            <ul className="plus-upgrade-feature-list">
              <li>{t("plus.welcomeFeatureVoice")}</li>
              <li>{t("plus.welcomeFeatureBurst")}</li>
              <li>{t("plus.welcomeFeatureAds")}</li>
            </ul>
            <div className="plus-upgrade-actions">
              <button className="secondary-button" type="button" disabled={billingBusy} onClick={() => openCustomerPortal({ source: "checkout_success_manage" })}>
                {billingBusy ? t("plus.opening") : t("plus.manage")}
              </button>
              <button className="primary-button" type="button" onClick={() => setPlusSuccessOpen(false)}>
                {t("plus.startPracticing")}
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </PlusContext.Provider>
  );
}

export function usePlus() {
  const context = useContext(PlusContext);
  if (!context) {
    throw new Error("usePlus must be used inside PlusProvider.");
  }
  return context;
}
